// ✅ ApplicantDetails.jsx — Mortgage Matrix (Refinance Flow)
import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./RefinanceForm.module.css";

const MERCURY_API_URL =
  "https://duh7zoxbvd.execute-api.ap-southeast-2.amazonaws.com/default/MortgageMatrixSendToMercury";
const LOOKUP_URL =
  "https://7r69rk0g8a.execute-api.ap-southeast-2.amazonaws.com/default/MortgageMatrixGetLeadByMobile-API";

function ApplicantDetails({ formData, setFormData, nextStep }) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [prefilling, setPrefilling] = useState(false);

  // ---------- Prefill from Mercury ----------
  const fetchExistingFromMercury = async (mobile) => {
    if (!mobile) return;
    setPrefilling(true);
    try {
      const resp = await fetch(LOOKUP_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile }),
      });
      const data = await resp.json();

      if (data?.success && data?.found) {
        console.log("✅ Mercury result:", data);

        // ✅ Always prefill Applicant 1
        let newState = {
          ...formData,
          applicant1: {
            ...(formData.applicant1 || {}),
            firstName: data.firstName || "",
            lastName: data.lastName || "",
            email: data.email || "",
            phone: data.mobile || mobile,
          },
          mercuryContactId: data.contactId || formData.mercuryContactId,
          mercuryOpportunityId:
            data.opportunityId || formData.mercuryOpportunityId,
        };

        // ✅ If co-applicant exists, prefill & toggle Yes automatically
        if (data.coApplicant) {
          console.log("👥 Co-applicant detected:", data.coApplicant);
          newState = {
            ...newState,
            refinancingWithSomeone: true,
            applicant2: {
              ...(formData.applicant2 || {}),
              firstName: data.coApplicant.firstName || "",
              lastName: data.coApplicant.lastName || "",
              email: data.coApplicant.email || "",
              phone: data.coApplicant.mobile || "",
            },
          };
        }

        setFormData(newState);

        toast.info(
          data.coApplicant
            ? "Existing clients found — both applicants prefilled."
            : "Existing client found — details prefilled."
        );
      } else {
        toast.warn("No existing client found in Mercury.");
      }
    } catch (e) {
      console.error("Prefill lookup failed:", e);
      toast.error("Error fetching from Mercury.");
    } finally {
      setPrefilling(false);
    }
  };

  // ---------- Auto-prefill on first load ----------
  useEffect(() => {
    const phone = formData?.applicant1?.phone || "";
    const first = formData?.applicant1?.firstName || "";
    const last = formData?.applicant1?.lastName || "";
    const email = formData?.applicant1?.email || "";
    if (!phone || (first || last || email)) return;
    fetchExistingFromMercury(phone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.applicant1?.phone]);

  // ---------- Field Updates ----------
  const handleChange = (field, value, applicant = "applicant1") => {
    setFormData({
      ...formData,
      [applicant]: { ...(formData[applicant] || {}), [field]: value.toString() },
    });
  };

  const handleRefinancingWithSomeone = (value) => {
    setFormData({
      ...formData,
      refinancingWithSomeone: Boolean(value),
      applicant2: value ? formData.applicant2 || {} : {},
    });
  };

  // ---------- Mercury Push ----------
  const sendLeadToMercury = async () => {
    try {
      setLoading(true);
      const res = await fetch(MERCURY_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (data.success)
        toast.success(`✅ Lead synced with Mercury (${data.tag || "Updated"})`);
      else toast.error("❌ Mercury sync failed");
    } catch (err) {
      console.error("❌ Mercury sync error:", err);
      toast.error("Error syncing with Mercury");
    } finally {
      setLoading(false);
    }
  };

  // ---------- Validation ----------
  const validateApplicant = (
    applicant,
    includeMaritalStatus = true,
    includeDependants = true
  ) => {
    const data = formData[applicant] || {};
    const validations = {
      firstName: !!data.firstName && data.firstName.trim().length > 1,
      lastName: !!data.lastName && data.lastName.trim().length > 0,
      email: !!data.email && /\S+@\S+\.\S+/.test(data.email),
    };
    if (includeMaritalStatus) validations.maritalStatus = !!data.maritalStatus;
    if (includeDependants) validations.dependants = !!data.dependants;
    return validations;
  };

  const applicant1Valid = validateApplicant("applicant1", true, true);
  const isValidApplicant1 = Object.values(applicant1Valid).every(Boolean);
  const applicant2Valid =
    formData.refinancingWithSomeone === true
      ? validateApplicant("applicant2", false, false)
      : { firstName: true, lastName: true, email: true };
  const isValidApplicant2 = Object.values(applicant2Valid).every(Boolean);
  const isFormValid = isValidApplicant1 && isValidApplicant2;

  const handleNext = async () => {
    setSubmitted(true);
    if (isFormValid) {
      await sendLeadToMercury();
      nextStep();
    } else {
      toast.error("⚠️ Please complete all required fields");
    }
  };

  // ---------- UI ----------
  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <h3>Applicant 1 Details</h3>
        <button
          type="button"
          onClick={() =>
            fetchExistingFromMercury(formData?.applicant1?.phone || "")
          }
          className={styles.refreshBtn}
          disabled={prefilling}
        >
          {prefilling ? "Refreshing..." : "↻ Refresh Prefill"}
        </button>
      </div>

      <div className={styles.formRow}>
        <input
          type="text"
          placeholder="First Name"
          value={formData.applicant1?.firstName || ""}
          onChange={(e) =>
            handleChange("firstName", e.target.value, "applicant1")
          }
        />
        <input
          type="text"
          placeholder="Last Name"
          value={formData.applicant1?.lastName || ""}
          onChange={(e) =>
            handleChange("lastName", e.target.value, "applicant1")
          }
        />
        <input
          type="email"
          placeholder="you@example.com"
          value={formData.applicant1?.email || ""}
          onChange={(e) => handleChange("email", e.target.value, "applicant1")}
        />

        <select
          value={formData.applicant1?.maritalStatus || ""}
          onChange={(e) =>
            handleChange("maritalStatus", e.target.value, "applicant1")
          }
        >
          <option value="">Select Marital Status</option>
          <option value="Single">Single</option>
          <option value="Married">Married</option>
          <option value="Defacto">Defacto</option>
        </select>

        <select
          value={formData.applicant1?.dependants || ""}
          onChange={(e) =>
            handleChange("dependants", e.target.value, "applicant1")
          }
        >
          <option value="">Number of Dependants</option>
          <option value="0">0</option>
          <option value="1">1</option>
          <option value="2">2</option>
          <option value="3+">3+</option>
        </select>
      </div>

      <p>Are you refinancing with someone else?</p>
      <div className={styles.buttonGroup}>
        <button
          type="button"
          className={
            formData.refinancingWithSomeone === true ? styles.activeBtn : ""
          }
          onClick={() => handleRefinancingWithSomeone(true)}
        >
          Yes
        </button>
        <button
          type="button"
          className={
            formData.refinancingWithSomeone === false ? styles.activeBtn : ""
          }
          onClick={() => handleRefinancingWithSomeone(false)}
        >
          No
        </button>
      </div>

      {formData.refinancingWithSomeone === true && (
        <div className={styles.formRow}>
          <h4>Applicant 2 Details</h4>
          <input
            type="text"
            placeholder="First Name"
            value={formData.applicant2?.firstName || ""}
            onChange={(e) =>
              handleChange("firstName", e.target.value, "applicant2")
            }
          />
          <input
            type="text"
            placeholder="Last Name"
            value={formData.applicant2?.lastName || ""}
            onChange={(e) =>
              handleChange("lastName", e.target.value, "applicant2")
            }
          />
          <input
            type="email"
            placeholder="you@example.com"
            value={formData.applicant2?.email || ""}
            onChange={(e) =>
              handleChange("email", e.target.value, "applicant2")
            }
          />
          <input
            type="tel"
            placeholder="04xxxxxxxx"
            value={formData.applicant2?.phone || ""}
            onChange={(e) => handleChange("phone", e.target.value, "applicant2")}
            maxLength="10"
          />
        </div>
      )}

      <div className={styles.navBtns}>
        <button
          className={`${styles.nextBtn} ${styles.fullWidthBtn}`}
          onClick={handleNext}
          disabled={!isFormValid || loading}
        >
          {loading ? "Saving..." : "Next →"}
        </button>
      </div>

      <ToastContainer position="top-center" autoClose={3000} hideProgressBar />
    </div>
  );
}

export default ApplicantDetails;
